/* =============================================================================
 * M.M.LaFleur — slide-out cart.
 *
 * Opens on any link to /cart, on quantity changes, and after an add to cart.
 *
 * Three things worth knowing about the approach:
 *
 *   1. Nothing here edits mm-editorial.js. Add-to-cart already lives there and
 *      posts to /cart/add.js; rather than rewrite a 21KB file to fire an event,
 *      this wraps window.fetch and watches for those posts. Contained, and it
 *      also catches any other code path that adds to the cart.
 *
 *   2. Only the contents region is re-rendered, by fetching
 *      /cart?view=mm-drawer. The checkout form and Shopify's express payment
 *      buttons are left alone — they bind listeners on page load and replacing
 *      their markup leaves dead buttons.
 *
 *   3. Line items are addressed by line number, not variant id, so a cart
 *      holding the same variant twice (different properties) still updates the
 *      right row.
 *
 *   4. Clicks are handled in the CAPTURE phase, not the bubble phase. The theme
 *      runs Barba page transitions, which binds its own bubble-phase click
 *      listener on document to intercept links. A bubble-phase preventDefault
 *      here comes too late: Barba has already committed to navigating, so
 *      opening the drawer from the bag icon ALSO soft-navigated to /cart
 *      underneath it, and closing the drawer revealed the cart page instead of
 *      the page you were on. Capturing on document runs before any bubble-phase
 *      listener, and stopPropagation there keeps the event from ever reaching
 *      Barba. Propagation is only stopped for clicks this file actually
 *      handles, so other delegated listeners are unaffected.
 * ========================================================================== */

(function () {
  "use strict";

  var root = null;
  var panel = null;
  var body = null;
  var lastFocus = null;
  var busy = false;

  function el(sel) { return root ? root.querySelector(sel) : null; }

  function init() {
    root = document.querySelector("[data-mm-cart]");
    if (!root) return false;
    panel = el(".mm-cart-panel");
    body = el("[data-mm-cart-body]");
    return true;
  }

  /* --- open / close ------------------------------------------------------ */

  function open() {
    if (!root && !init()) return;
    if (root.getAttribute("data-open") === "true") return;
    lastFocus = document.activeElement;
    root.hidden = false;
    /* Next frame, so the panel has a rendered starting transform to slide from
       rather than appearing already in place. */
    window.requestAnimationFrame(function () {
      root.setAttribute("data-open", "true");
    });
    document.body.classList.add("mm-no-scroll");
    var close = el(".mm-cart-close");
    if (close) close.focus();
  }

  function close() {
    if (!root || root.hidden) return;
    root.setAttribute("data-open", "false");
    document.body.classList.remove("mm-no-scroll");
    window.setTimeout(function () {
      if (root.getAttribute("data-open") === "false") root.hidden = true;
    }, 400);
    if (lastFocus && document.contains(lastFocus)) lastFocus.focus();
    lastFocus = null;
  }

  function isOpen() {
    return root && !root.hidden && root.getAttribute("data-open") === "true";
  }

  /* --- rendering -------------------------------------------------------- */

  function applyState() {
    var node = body.querySelector("[data-mm-cart-state]");
    if (!node) return;
    var state;
    try {
      state = JSON.parse(node.textContent);
    } catch (err) {
      return;
    }

    var count = el("[data-mm-cart-count]");
    if (count) count.textContent = String(state.count);

    var subtotal = el("[data-mm-cart-subtotal]");
    if (subtotal) subtotal.innerHTML = state.subtotalText;

    panel.setAttribute("data-empty", state.empty ? "true" : "false");

    /* Keep the header bag count in sync with the drawer. The header markup
       predates this drawer, so match generously rather than assume one hook. */
    var badges = document.querySelectorAll("[data-mm-cart-count-badge], .mm-bag-count");
    Array.prototype.forEach.call(badges, function (b) {
      b.textContent = String(state.count);
    });
  }

  function refresh() {
    return fetch(window.Shopify && window.Shopify.routes && window.Shopify.routes.root
        ? window.Shopify.routes.root + "cart?view=mm-drawer"
        : "/cart?view=mm-drawer", { credentials: "same-origin" })
      .then(function (r) { return r.text(); })
      .then(function (html) {
        body.innerHTML = html;
        applyState();
      })
      .catch(function () { /* leave the drawer as-is rather than blanking it */ });
  }

  /* --- mutations -------------------------------------------------------- */

  function changeLine(line, quantity) {
    if (busy) return;
    busy = true;
    panel.setAttribute("data-busy", "true");
    fetch("/cart/change.js", {
      method: "POST",
      credentials: "same-origin",
      headers: { "Content-Type": "application/json", "Accept": "application/json" },
      body: JSON.stringify({ line: line, quantity: quantity })
    })
      .then(function () { return refresh(); })
      .finally(function () {
        busy = false;
        panel.setAttribute("data-busy", "false");
      });
  }

  /* --- wiring ----------------------------------------------------------- */

  function handled(e) {
    e.preventDefault();
    /* Capture phase: this keeps the click from ever reaching Barba's
       bubble-phase link handler, which would otherwise navigate. */
    e.stopPropagation();
  }

  document.addEventListener("click", function (e) {
    if (!root && !init()) return;
    if (!e.target || !e.target.closest) return;

    if (e.target.closest("[data-mm-cart-close]")) {
      handled(e);
      close();
      return;
    }

    var qty = e.target.closest("[data-mm-cart-qty]");
    if (qty) {
      handled(e);
      var row = qty.closest("[data-mm-cart-line]");
      if (!row) return;
      changeLine(
        parseInt(row.getAttribute("data-mm-cart-line"), 10),
        parseInt(qty.getAttribute("data-mm-cart-qty"), 10)
      );
      return;
    }

    /* Any link to the cart opens the drawer instead of navigating. Modified
       clicks fall through untouched, so cmd-click still opens the cart page in
       a new tab and the drawer keeps a working no-JS fallback. */
    var cartLink = e.target.closest('a[href$="/cart"], a[href*="/cart?"]');
    if (cartLink && !e.metaKey && !e.ctrlKey && !e.shiftKey && !e.altKey && e.button === 0) {
      handled(e);
      open();
      refresh();
    }
  }, true);

  document.addEventListener("keydown", function (e) {
    if (!isOpen()) return;
    if (e.key === "Escape") { e.preventDefault(); close(); return; }
    if (e.key !== "Tab") return;

    var stops = panel.querySelectorAll(
      'a[href], button:not([disabled]), input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    stops = Array.prototype.filter.call(stops, function (n) { return n.offsetParent !== null; });
    if (!stops.length) return;
    var first = stops[0];
    var last = stops[stops.length - 1];
    if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
  });

  /* Catch adds to cart wherever they come from. mm-editorial.js posts to
     /cart/add.js from the PDP and the quick-add modal; rather than couple to
     either, watch the network call itself. */
  var nativeFetch = window.fetch;
  window.fetch = function () {
    var args = arguments;
    var url = args[0];
    if (url && url.url) url = url.url;
    var isAdd = typeof url === "string" && url.indexOf("/cart/add") !== -1;

    return nativeFetch.apply(this, args).then(function (response) {
      if (isAdd && response.ok) {
        if (root || init()) {
          refresh().then(open);
        }
      }
      return response;
    });
  };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
}());
